import React, { useState } from 'react';
import { CheckCircle2, XCircle, Calculator, IndianRupee, Users, Briefcase, Info } from 'lucide-react';

// Static option lists (extracted from inline JSX to avoid creating new
// array references on every render, which would cause Select to re-mount)
const PARENT_OCCUPATION_OPTIONS = [
  { v: 'not-govt', l: 'Non-government / private / self-employed' },
  { v: 'class-2', l: 'Class II (Group B) officer' },
  { v: 'class-1', l: 'Class I (Group A) officer' },
  { v: 'class-1-pre-40', l: 'Class I officer (reached before age 40)' },
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { v: 'government', l: 'Central/State Government (fully exempt)' },
  { v: 'private', l: 'Private sector / PSU' },
];

const Section = ({ title, children, testid }) => (
  <div data-testid={testid} className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm">
    {title}
    {children}
  </div>
);

const Result = ({ ok, title, detail }) => (
  <div
    data-testid="calc-result"
    className={`mt-5 p-4 rounded-xl border-2 ${
      ok ? 'bg-green-50 border-green-300 text-green-900' : 'bg-red-50 border-red-300 text-red-900'
    } flex items-start gap-3`}
  >
    {ok ? <CheckCircle2 size={22} className="shrink-0 mt-0.5" /> : <XCircle size={22} className="shrink-0 mt-0.5" />}
    <div>
      <div className="font-semibold">{title}</div>
      <div className="text-sm mt-1 opacity-90">{detail}</div>
    </div>
  </div>
);

// 1. EWS Eligibility Checker
const EWSChecker = () => {
  const [income, setIncome] = useState('');
  const [agriAcres, setAgriAcres] = useState('');
  const [flatSqFt, setFlatSqFt] = useState('');
  const [plotMunicipalSqYd, setPlotMunicipalSqYd] = useState('');
  const [plotNonMunicipalSqYd, setPlotNonMunicipalSqYd] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const check = () => {
    setSubmitted(true);
  };

  const reset = () => {
    setIncome('');
    setAgriAcres('');
    setFlatSqFt('');
    setPlotMunicipalSqYd('');
    setPlotNonMunicipalSqYd('');
    setSubmitted(false);
  };

  const inc = parseFloat(income) || 0;
  const agri = parseFloat(agriAcres) || 0;
  const flat = parseFloat(flatSqFt) || 0;
  const muniPlot = parseFloat(plotMunicipalSqYd) || 0;
  const nonMuniPlot = parseFloat(plotNonMunicipalSqYd) || 0;

  const incomeOk = inc < 800000;
  const agriOk = agri < 5;
  const flatOk = flat < 1000;
  const muniOk = muniPlot < 100;
  const nonMuniOk = nonMuniPlot < 200;

  const eligible = incomeOk && agriOk && flatOk && muniOk && nonMuniOk;

  return (
    <Section
      testid="ews-checker"
      title={
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-700"><Calculator size={22} /></div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">EWS Eligibility Checker</h3>
            <p className="text-sm text-gray-600 mt-1">Check if you qualify for 10% EWS reservation. Based on official DoPT criteria (income &lt; ₹8 lakh + asset limits).</p>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Family gross annual income (₹)" testid="ews-income" value={income} onChange={setIncome} placeholder="e.g. 600000" suffix="₹/yr" />
        <Field label="Agricultural land owned" testid="ews-agri" value={agriAcres} onChange={setAgriAcres} placeholder="e.g. 2.5" suffix="acres" />
        <Field label="Residential flat size" testid="ews-flat" value={flatSqFt} onChange={setFlatSqFt} placeholder="e.g. 850" suffix="sq ft" />
        <Field label="Residential plot in notified municipality" testid="ews-muni-plot" value={plotMunicipalSqYd} onChange={setPlotMunicipalSqYd} placeholder="e.g. 80" suffix="sq yd" />
        <Field label="Residential plot in non-notified area" testid="ews-nonmuni-plot" value={plotNonMunicipalSqYd} onChange={setPlotNonMunicipalSqYd} placeholder="e.g. 150" suffix="sq yd" />
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={check} data-testid="ews-check-btn" className="px-5 py-2.5 rounded-full bg-orange-600 text-white font-semibold text-sm hover:bg-orange-700 transition">
          Check eligibility
        </button>
        <button onClick={reset} data-testid="ews-reset-btn" className="px-5 py-2.5 rounded-full bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-gray-200 transition">
          Reset
        </button>
      </div>

      {submitted && (
        <>
          <Result
            ok={eligible}
            title={eligible ? 'Likely Eligible for EWS' : 'Not Eligible for EWS'}
            detail={
              eligible
                ? 'Based on your inputs, you meet the income and asset criteria. Apply to your Tehsildar/SDM with income proof.'
                : 'You exceed at least one criterion below. EWS reservation will not apply.'
            }
          />
          <ul className="mt-4 space-y-2 text-sm">
            <Bullet ok={incomeOk} text={`Income ${incomeOk ? '✓' : '✗'} — Limit is ₹8,00,000 per year`} />
            <Bullet ok={agriOk} text={`Agricultural land ${agriOk ? '✓' : '✗'} — Limit is below 5 acres`} />
            <Bullet ok={flatOk} text={`Residential flat ${flatOk ? '✓' : '✗'} — Limit is below 1,000 sq ft`} />
            <Bullet ok={muniOk} text={`Municipal plot ${muniOk ? '✓' : '✗'} — Limit is below 100 sq yd`} />
            <Bullet ok={nonMuniOk} text={`Non-municipal plot ${nonMuniOk ? '✓' : '✗'} — Limit is below 200 sq yd`} />
          </ul>
        </>
      )}
      <p className="mt-5 text-xs text-gray-500 flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" />This checker is for guidance only. Final eligibility is determined by the issuing Tehsildar/SDM as per DoPT Office Memorandum.</p>
    </Section>
  );
};

// 2. OBC NCL Eligibility Checker
const OBCNCLChecker = () => {
  const [income, setIncome] = useState('');
  const [fatherClass, setFatherClass] = useState('not-govt');
  const [motherClass, setMotherClass] = useState('not-govt');
  const [submitted, setSubmitted] = useState(false);

  const check = () => setSubmitted(true);
  const reset = () => {
    setIncome('');
    setFatherClass('not-govt');
    setMotherClass('not-govt');
    setSubmitted(false);
  };

  const inc = parseFloat(income) || 0;
  const incomeOk = inc < 800000;
  const parentsCreamy = (fatherClass === 'class-1' && motherClass === 'class-1') || fatherClass === 'class-1-pre-40' || motherClass === 'class-1-pre-40';
  const eligible = incomeOk && !parentsCreamy;

  return (
    <Section
      testid="obc-checker"
      title={
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700"><Users size={22} /></div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">OBC Non-Creamy Layer Checker</h3>
            <p className="text-sm text-gray-600 mt-1">Indra Sawhney + DoPT criteria — combined parental income &lt; ₹8 lakh and parental occupation rules.</p>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Parents&apos; combined gross income (₹)" testid="obc-income" value={income} onChange={setIncome} placeholder="e.g. 700000" suffix="₹/yr" />
        <Select label="Father&apos;s occupation" testid="obc-father" value={fatherClass} onChange={setFatherClass} options={PARENT_OCCUPATION_OPTIONS} />
        <Select label="Mother&apos;s occupation" testid="obc-mother" value={motherClass} onChange={setMotherClass} options={PARENT_OCCUPATION_OPTIONS} />
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={check} data-testid="obc-check-btn" className="px-5 py-2.5 rounded-full bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition">
          Check eligibility
        </button>
        <button onClick={reset} data-testid="obc-reset-btn" className="px-5 py-2.5 rounded-full bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-gray-200 transition">
          Reset
        </button>
      </div>

      {submitted && (
        <Result
          ok={eligible}
          title={eligible ? 'Likely Non-Creamy Layer (Eligible for 27% OBC reservation)' : 'Falls into Creamy Layer'}
          detail={
            eligible
              ? 'Apply for OBC NCL certificate at your Tehsildar/SDM with parents&apos; income proof. Valid for one financial year.'
              : !incomeOk
                ? 'Combined parental income exceeds ₹8 lakh — falls in creamy layer.'
                : 'Parental occupation places you in the creamy layer (both parents Class I, or Class I pre-40).'
          }
        />
      )}
    </Section>
  );
};

// 3. CCS Commuted Leave Calculator
const CommutedLeaveCalc = () => {
  const [hplBalance, setHplBalance] = useState('');
  const [daysNeeded, setDaysNeeded] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const balance = parseFloat(hplBalance) || 0;
  const days = parseFloat(daysNeeded) || 0;
  const debit = days * 2;
  const maxPossible = Math.floor(balance / 2);
  const possible = debit <= balance;
  const lifeTimeCap = 180;
  const exceedsLifetime = days > lifeTimeCap;

  return (
    <Section
      testid="commuted-leave"
      title={
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center text-green-700"><Briefcase size={22} /></div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">CCS Commuted Leave Calculator</h3>
            <p className="text-sm text-gray-600 mt-1">For central government employees — converts Half Pay Leave to Full Pay Leave by debiting 2× days. Max 180 days during entire service.</p>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Your current HPL balance (days)" testid="cl-balance" value={hplBalance} onChange={setHplBalance} placeholder="e.g. 60" suffix="days" />
        <Field label="Commuted leave days you need" testid="cl-needed" value={daysNeeded} onChange={setDaysNeeded} placeholder="e.g. 20" suffix="days" />
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={() => setSubmitted(true)} data-testid="cl-calc-btn" className="px-5 py-2.5 rounded-full bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition">
          Calculate
        </button>
        <button onClick={() => { setHplBalance(''); setDaysNeeded(''); setSubmitted(false); }} data-testid="cl-reset-btn" className="px-5 py-2.5 rounded-full bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-gray-200 transition">
          Reset
        </button>
      </div>

      {submitted && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Days requested" value={days || 0} unit="days" testid="cl-stat-requested" />
          <Stat label="HPL to be debited" value={debit || 0} unit="days" tone="amber" testid="cl-stat-debit" />
          <Stat label="Max commuted leave possible" value={maxPossible || 0} unit="days" tone="blue" testid="cl-stat-max" />
        </div>
      )}

      {submitted && (
        <Result
          ok={possible && !exceedsLifetime}
          title={
            exceedsLifetime
              ? 'Exceeds lifetime cap of 180 days'
              : possible
                ? 'You can take this commuted leave'
                : `Insufficient HPL — you need ${debit} days but have ${balance}`
          }
          detail={
            exceedsLifetime
              ? 'CCS Rule 30 limits commuted leave to 180 days during entire service (240 days for permanent disability).'
              : possible
                ? `Your HPL balance after the leave will be ${balance - debit} days. Don\u2019t forget to attach AMA medical certificate.`
                : `You can take a maximum of ${maxPossible} days commuted leave with your current HPL.`
          }
        />
      )}
    </Section>
  );
};

// 4. Leave Encashment Tax Calculator (Section 10(10AA))
const LeaveEncashmentCalc = () => {
  const [type, setType] = useState('private');
  const [salary, setSalary] = useState('');
  const [received, setReceived] = useState('');
  const [serviceYears, setServiceYears] = useState('');
  const [leaveDays, setLeaveDays] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const calc = () => {
    setSubmitted(true);
  };

  const reset = () => {
    setSalary('');
    setReceived('');
    setServiceYears('');
    setLeaveDays('');
    setSubmitted(false);
  };

  const monthly = parseFloat(salary) || 0;
  const rec = parseFloat(received) || 0;
  const yrs = parseFloat(serviceYears) || 0;
  const days = parseFloat(leaveDays) || 0;

  const tenMonthsAvg = monthly * 10;
  const unavailedCap = Math.min(days, yrs * 30) * (monthly / 30);
  const statutoryCap = 2500000;
  const exempt = type === 'government' ? rec : Math.min(rec, statutoryCap, tenMonthsAvg, unavailedCap);
  const taxable = Math.max(0, rec - exempt);

  return (
    <Section
      testid="leave-encash"
      title={
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center text-purple-700"><IndianRupee size={22} /></div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Leave Encashment Tax Calculator</h3>
            <p className="text-sm text-gray-600 mt-1">Section 10(10AA) of the Income Tax Act — calculate how much of your retirement leave encashment is tax-exempt vs taxable.</p>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select label="Employment type" testid="le-type" value={type} onChange={setType} options={EMPLOYMENT_TYPE_OPTIONS} />
        <Field label="Average monthly salary (₹) — basic + DA" testid="le-salary" value={salary} onChange={setSalary} placeholder="e.g. 80000" suffix="₹/mo" />
        <Field label="Encashment amount received (₹)" testid="le-received" value={received} onChange={setReceived} placeholder="e.g. 2000000" suffix="₹" />
        <Field label="Years of service" testid="le-years" value={serviceYears} onChange={setServiceYears} placeholder="e.g. 28" suffix="years" />
        <Field label="Total unavailed leave (days)" testid="le-days" value={leaveDays} onChange={setLeaveDays} placeholder="e.g. 240" suffix="days" />
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={calc} data-testid="le-calc-btn" className="px-5 py-2.5 rounded-full bg-purple-600 text-white font-semibold text-sm hover:bg-purple-700 transition">
          Calculate
        </button>
        <button onClick={reset} data-testid="le-reset-btn" className="px-5 py-2.5 rounded-full bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-gray-200 transition">
          Reset
        </button>
      </div>

      {submitted && (
        <>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat label="Total received" value={`₹${rec.toLocaleString('en-IN')}`} testid="le-stat-received" />
            <Stat label="Tax-exempt portion" value={`₹${Math.round(exempt).toLocaleString('en-IN')}`} tone="blue" testid="le-stat-exempt" />
            <Stat label="Taxable portion" value={`₹${Math.round(taxable).toLocaleString('en-IN')}`} tone="amber" testid="le-stat-taxable" />
          </div>
          {type === 'private' && (
            <div className="mt-4 p-4 rounded-lg bg-purple-50 border border-purple-200 text-sm text-purple-900">
              <p className="font-semibold mb-2">How we calculated (lower of 4 limits):</p>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                <li>Actual received: ₹{rec.toLocaleString('en-IN')}</li>
                <li>Statutory cap: ₹{statutoryCap.toLocaleString('en-IN')} (2023 notification)</li>
                <li>10 × monthly avg salary: ₹{Math.round(tenMonthsAvg).toLocaleString('en-IN')}</li>
                <li>Unavailed leave cash equivalent (max 30 days/year): ₹{Math.round(unavailedCap).toLocaleString('en-IN')}</li>
              </ul>
            </div>
          )}
          {type === 'government' && (
            <div className="mt-4 p-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-900">
              Government employees enjoy <strong>full exemption</strong> under Section 10(10AA)(i) — entire encashment is tax-free.
            </div>
          )}
        </>
      )}
      <p className="mt-5 text-xs text-gray-500 flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" />For guidance only. Always consult a chartered accountant for your final tax computation.</p>
    </Section>
  );
};

// Small reusable inputs
const Field = ({ label, value, onChange, placeholder, suffix, testid }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700">{label}</span>
    <div className="mt-1.5 relative">
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full px-3 py-2.5 pr-16 rounded-lg border border-gray-300 focus:border-gray-900 focus:ring-2 focus:ring-gray-200 outline-none transition text-sm"
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">{suffix}</span>}
    </div>
  </label>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-gray-900 focus:ring-2 focus:ring-gray-200 outline-none transition text-sm bg-white"
    >
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </label>
);

const Bullet = ({ ok, text }) => (
  <li className={`flex items-center gap-2 ${ok ? 'text-green-700' : 'text-red-700'}`}>
    {ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
    <span>{text}</span>
  </li>
);

const Stat = ({ label, value, unit, tone = 'gray', testid }) => {
  const tones = {
    gray: 'bg-gray-50 border-gray-200 text-gray-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
  };
  return (
    <div data-testid={testid} className={`p-4 rounded-xl border ${tones[tone]}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}{unit && <span className="text-sm font-normal opacity-70 ml-1">{unit}</span>}</div>
    </div>
  );
};

const Calculators = () => {
  return (
    <section data-testid="govt-calculators" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium mb-3">
            <Calculator size={14} /> Interactive Tools
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">Eligibility checkers &amp; calculators</h2>
          <p className="mt-2 text-base text-gray-600 max-w-2xl">Plug in your numbers and find out whether you qualify — for EWS, OBC NCL, commuted leave or how much leave encashment is tax-free.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EWSChecker />
        <OBCNCLChecker />
        <CommutedLeaveCalc />
        <LeaveEncashmentCalc />
      </div>
    </section>
  );
};

export default Calculators;
